import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionShell, SCard, VerifiedBadge, TIMEZONES } from "./SettingsUI";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang, translateFr as tfr } from "@/i18n";

const ChangeEmailDialog = ({ open, onOpenChange, hasPassword, reload }) => {
  const { tf } = useLang();
  const [step, setStep] = useState(0);
  const [password, setPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const reset = () => { setStep(0); setPassword(""); setNewEmail(""); setCode(""); };

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/account/email/change-start", { password: password || null, new_email: newEmail });
      toast.success(tf("Code envoyé à la nouvelle adresse"));
      setStep(1);
      setCooldown(60);
      const t = setInterval(() => setCooldown((c) => { if (c <= 1) clearInterval(t); return c - 1; }), 1000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/account/email/change-verify", { code });
      toast.success(tf("Adresse email mise à jour"));
      onOpenChange(false); reset(); reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[440px]" data-testid="change-email-dialog">
        <DialogHeader>
          <DialogTitle>{tf("Changer l'adresse email")}</DialogTitle>
          <DialogDescription>
            {step === 0 ? tf("Un code de vérification à 6 chiffres sera envoyé à la nouvelle adresse. Votre email actuel reste actif tant que la vérification n'est pas terminée.")
              : `${tf("Saisissez le code envoyé à")} ${newEmail}.`}
          </DialogDescription>
        </DialogHeader>
        {step === 0 ? (
          <div className="grid gap-4">
            {hasPassword && (
              <div className="grid gap-2">
                <Label>{tf("Mot de passe actuel")}</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="email-change-password" autoComplete="current-password" />
              </div>
            )}
            <div className="grid gap-2">
              <Label>{tf("Nouvelle adresse email")}</Label>
              <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder={tfr("nouvelle@adresse.fr")} data-testid="email-change-new-input" />
            </div>
            <Button onClick={start} disabled={busy || !newEmail || (hasPassword && !password)} data-testid="email-change-send-btn">
              {busy ? tf("Envoi…") : tf("Envoyer le code de vérification")}
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" inputMode="numeric" maxLength={6}
              className="text-center text-lg tracking-[0.4em]" data-testid="email-change-code-input" />
            <Button onClick={verify} disabled={busy || code.length !== 6} data-testid="email-change-verify-btn">
              {busy ? tf("Vérification…") : tf("Vérifier et mettre à jour")}
            </Button>
            <button type="button" disabled={cooldown > 0 || busy} onClick={start}
              className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 mx-auto" data-testid="email-change-resend-btn">
              {cooldown > 0 ? `${tf("Renvoyer le code dans")} ${cooldown}s` : tf("Renvoyer le code")}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const SectionAccount = ({ overview, reload }) => {
  const { tf, setLang } = useLang();
  const p = overview?.profile || {};
  const [form, setForm] = useState({
    first_name: p.first_name || "", last_name: p.last_name || "",
    phone: p.phone || "", preferred_language: p.preferred_language || "fr",
    timezone: p.timezone || "Europe/Paris",
  });
  const [saving, setSaving] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error(tf("Prénom et nom requis")); return; }
    setSaving(true);
    try {
      await api.patch("/account/profile", form);
      setLang(form.preferred_language);
      toast.success(form.preferred_language === "en" ? "Profile updated" : "Profil mis à jour");
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setSaving(false); }
  };

  return (
    <SectionShell title={tf("Compte")} desc={tf("Gérez vos informations personnelles et vos préférences de connexion.")} testid="section-account">
      <SCard title={tf("Informations personnelles")} testid="account-info-card">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label>{tf("Prénom")}</Label>
            <Input value={form.first_name} onChange={(e) => set("first_name")(e.target.value)} data-testid="account-firstname-input" />
          </div>
          <div className="grid gap-2">
            <Label>{tf("Nom")}</Label>
            <Input value={form.last_name} onChange={(e) => set("last_name")(e.target.value)} data-testid="account-lastname-input" />
          </div>
          <div className="grid gap-2">
            <Label className="flex items-center gap-2">{tf("Email professionnel")} <VerifiedBadge verified={p.email_verified} /></Label>
            <div className="flex items-center gap-2">
              <Input value={p.email || ""} disabled data-testid="account-email-input" className="flex-1 disabled:opacity-90 text-foreground" />
              <Button variant="outline" className="h-10 shrink-0" onClick={() => setEmailDialog(true)} data-testid="change-email-btn">{tf("Changer")}</Button>
            </div>
          </div>
          <div className="grid gap-2">
            <Label>{tf("Téléphone")}</Label>
            <Input value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="+33 6…" data-testid="account-phone-input" />
          </div>
          <div className="grid gap-2">
            <Label>{tf("Langue préférée")}</Label>
            <Select value={form.preferred_language} onValueChange={set("preferred_language")}>
              <SelectTrigger data-testid="account-language-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">{tfr("Français")}</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>{tf("Fuseau horaire")}</Label>
            <Select value={form.timezone} onValueChange={set("timezone")}>
              <SelectTrigger data-testid="account-timezone-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tfr(tz)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end mt-5">
          <Button onClick={save} disabled={saving} data-testid="account-save-btn">{saving ? tf("Enregistrement…") : tf("Enregistrer")}</Button>
        </div>
      </SCard>
      <ChangeEmailDialog open={emailDialog} onOpenChange={setEmailDialog}
        hasPassword={overview?.security?.has_password} reload={reload} />
    </SectionShell>
  );
};
